// What BaseLoadingWithText says, and when. Pure so the timing ladder and the context lines can be
// tested without timers.
//
// The status line escalates with the wait: ready (0–5s) → almost (5–15s) → long (15–30s) →
// overdue (30s+). A screen that knows what it is loading passes a `context`, and the FIRST line
// names it ("Sonuçlar hesaplanıyor…"). Only the first: it is the one claim that is true the moment
// the loader appears, while a later "what we're doing now" line would be invented — the loader has
// no progress signal. The overdue line asks the user to report the problem, so it is never playful.
//
// Same ladder as catchup-admin-web. The tip list is the learner one: only the learning-science
// tips (retrieval, spacing, interleaving) — they speak to a student as much as to a teacher. The
// admin's product tip ("MEB bu hafta" on assignment creation) is teacher-only and is left out.

export type LoadingContext = "report" | "exam" | "content" | "ai";

export type LoadingStage = 0 | 1 | 2 | 3;

const STAGE_LIMITS_MS = [5000, 15000, 30000];

/** The "Biliyor muydunuz?" line shows from this point until the overdue stage takes over. */
export const LOADING_TIP_DELAY_MS = 5000;
/** How long one tip stays up before the next one. */
export const LOADING_TIP_INTERVAL_MS = 6000;

const GENERIC_LINE_KEYS = [
  "loading_line_ready",
  "loading_line_almost",
  "loading_line_long",
  "loading_line_overdue",
];

const CONTEXT_LINE_KEYS: Record<LoadingContext, string> = {
  report: "loading_line_report",
  exam: "loading_line_exam",
  content: "loading_line_content",
  ai: "loading_line_ai",
};

/** Each tip is a [label key, body key] pair; the label renders bold. */
export const LOADING_TIP_KEYS: readonly [string, string][] = [
  ["loading_tip_label_fact", "loading_tip_retrieval"],
  ["loading_tip_label_fact", "loading_tip_spacing"],
  ["loading_tip_label_fact", "loading_tip_interleaving"],
];

export const resolveLoadingStage = (elapsedMs: number): LoadingStage => {
  if (elapsedMs < STAGE_LIMITS_MS[0]) return 0;
  if (elapsedMs < STAGE_LIMITS_MS[1]) return 1;
  if (elapsedMs < STAGE_LIMITS_MS[2]) return 2;
  return 3;
};

export const resolveLoadingLineKey = (
  stage: LoadingStage,
  context?: LoadingContext,
): string => {
  if (context && stage === 0) return CONTEXT_LINE_KEYS[context];
  return GENERIC_LINE_KEYS[stage];
};

/**
 * The tip to show after `elapsedMs`, or null when none should show: before the delay (a quick
 * load never sees one) and once the overdue line is up (it must not compete with a call to act).
 * `offset` picks where in the list this loader starts, so the same tip does not open every wait.
 */
export const resolveLoadingTip = (
  elapsedMs: number,
  offset = 0,
): [string, string] | null => {
  if (elapsedMs < LOADING_TIP_DELAY_MS) return null;
  if (resolveLoadingStage(elapsedMs) === 3) return null;
  const step = Math.floor((elapsedMs - LOADING_TIP_DELAY_MS) / LOADING_TIP_INTERVAL_MS);
  const count = LOADING_TIP_KEYS.length;
  return LOADING_TIP_KEYS[(((step + offset) % count) + count) % count];
};
